type GuestSlug = 'ma' | 'james';
type GuestPreviewState =
  | 'create-pin'
  | 'enter-pin'
  | 'request-access'
  | 'guest-home'
  | 'text-channel'
  | 'call-channel'
  | 'screenshot-policy'
  | 'read-receipts-retention'
  | 'connection-security';
type MessageKind = 'user' | 'system';
type CallState = 'ready' | 'ringing' | 'connected' | 'reconnecting' | 'ended';

interface TestStateDef {
  value: GuestPreviewState;
  label: string;
}

interface GuestProfile {
  slug: GuestSlug;
  name: string;
  categoryName: string;
  textChannelName: string;
  callChannelName: string;
}

interface GuestMessage {
  id: string;
  kind: MessageKind;
  sender: 'Mitchell' | GuestProfile['name'] | 'System';
  content: string;
  createdAt: string;
  read: boolean;
  replyToId: string | null;
}

const GUEST_PREVIEW_BASE = '/people/test/guest-preview';
const TEST_STATES: TestStateDef[] = [
  { value: 'create-pin', label: 'First open / create PIN' },
  { value: 'enter-pin', label: 'Returning / enter PIN' },
  { value: 'request-access', label: 'Access disabled / request access' },
  { value: 'guest-home', label: 'Guest home' },
  { value: 'text-channel', label: 'Text channel' },
  { value: 'call-channel', label: 'Call channel' },
  { value: 'screenshot-policy', label: 'Screenshot policy' },
  { value: 'read-receipts-retention', label: 'Read receipts / retention' },
  { value: 'connection-security', label: 'Connection security' },
];

let currentState: GuestPreviewState = 'create-pin';
let pinValue = '';
let pinConfirmValue = '';
let pinError = '';
let requestMessage = '';
let requestSubmitted = false;
let messages: GuestMessage[] = [];
let composerValue = '';
let replyToId: string | null = null;
let callState: CallState = 'ready';
let callMuted = false;
let speakerEnabled = true;
let connectedStartedAt = 0;
let elapsedBeforeCurrentConnection = 0;
let endedDurationSeconds = 0;
let callTimerId: number | null = null;
let ringingTimerId: number | null = null;
let reconnectTimerId: number | null = null;
let renderTick = 0;

export function isGuestTestPath(): boolean {
  const path = window.location.pathname;
  return path === `${GUEST_PREVIEW_BASE}/ma` || path === `${GUEST_PREVIEW_BASE}/james`;
}

export function renderGuestTestPage(): void {
  mountGuestPreviewStyles();
  document.body.classList.add('guest-preview-test-body');
  currentState = 'create-pin';
  pinValue = '';
  pinConfirmValue = '';
  pinError = '';
  requestMessage = '';
  requestSubmitted = false;
  composerValue = '';
  replyToId = null;
  callState = 'ready';
  callMuted = false;
  speakerEnabled = true;
  connectedStartedAt = 0;
  elapsedBeforeCurrentConnection = 0;
  endedDurationSeconds = 0;
  clearCallTimers();
  messages = mockMessagesForGuest(guestForRoute());
  paint();
}

function paint(): void {
  const app = document.getElementById('app');
  if (!app) return;
  const guest = guestForRoute();
  app.innerHTML = `
    <main class="gpt-shell">
      ${renderTestBanner(guest)}
      ${renderStateSelector()}
      ${renderCurrentState(guest)}
    </main>
  `;
  wirePage();
}

function guestForRoute(): GuestProfile {
  const slug: GuestSlug = window.location.pathname.endsWith('/james') ? 'james' : 'ma';
  const name = slug === 'james' ? 'James' : 'Ma';
  return {
    slug,
    name,
    categoryName: name,
    textChannelName: `${name} text`,
    callChannelName: `${name} call`,
  };
}

function finalIntendedUrl(guest: GuestProfile): string {
  return `/the-hub-ecosystems/hub-people/${guest.slug}`;
}

function renderTestBanner(guest: GuestProfile): string {
  return `
    <section class="gpt-test-banner" aria-label="Guest preview test notice">
      <div class="gpt-test-kicker">Test preview only</div>
      <div class="gpt-test-copy">backend guest access not live — no real access until Mitchell approves</div>
      <div class="gpt-final-url"><span>Final intended URL</span><code>${escapeHtml(finalIntendedUrl(guest))}</code></div>
    </section>
  `;
}

function renderStateSelector(): string {
  return `
    <section class="gpt-state-panel" aria-label="Mitchell test state selector">
      <label for="gpt-state-select">Preview state</label>
      <div class="gpt-state-row">
        <select id="gpt-state-select">
          ${TEST_STATES.map((state) => `<option value="${escapeAttr(state.value)}" ${state.value === currentState ? 'selected' : ''}>${escapeHtml(state.label)}</option>`).join('')}
        </select>
        <button type="button" class="gpt-secondary-btn" id="gpt-prev-state" aria-label="Previous state">${iconChevronLeft()}</button>
        <button type="button" class="gpt-secondary-btn" id="gpt-next-state" aria-label="Next state">${iconChevronRight()}</button>
      </div>
    </section>
  `;
}

function renderCurrentState(guest: GuestProfile): string {
  if (currentState === 'create-pin') return renderPinCreate(guest);
  if (currentState === 'enter-pin') return renderPinEnter(guest);
  if (currentState === 'request-access') return renderRequestAccess(guest);
  if (currentState === 'guest-home') return renderGuestHome(guest);
  if (currentState === 'text-channel') return renderTextChannel(guest);
  if (currentState === 'call-channel') return renderCallChannel(guest);
  if (currentState === 'screenshot-policy') return renderNoticePage(guest, 'Screenshot restrictions', 'Screenshots are not permitted in this guest channel. Browser-level screenshot prevention is limited, and app-level capture notices will be shown where detectable.', iconShieldLock());
  if (currentState === 'read-receipts-retention') return renderNoticePage(guest, 'Read receipts and retention', 'Read receipts are enabled in the planned flow. 1-hour-after-read deletion is planned and will activate only after the backend retention worker is deployed.', iconDoubleCheck());
  return renderNoticePage(guest, 'Connection security', 'Guest access will use encrypted connections in transit. Calls use WebRTC encrypted media.', iconLock());
}

function renderPinCreate(guest: GuestProfile): string {
  return `
    <section class="gpt-card gpt-auth-card">
      <div class="gpt-auth-icon">${iconShieldLock()}</div>
      <h1>Create your PIN</h1>
      <p>Set a 4-6 digit PIN for Mitchell Space.</p>
      <label for="gpt-pin">PIN</label>
      <input id="gpt-pin" type="password" inputmode="numeric" autocomplete="new-password" maxlength="6" value="${escapeAttr(pinValue)}">
      <label for="gpt-pin-confirm">Confirm PIN</label>
      <input id="gpt-pin-confirm" type="password" inputmode="numeric" autocomplete="new-password" maxlength="6" value="${escapeAttr(pinConfirmValue)}">
      ${pinError ? `<div class="gpt-error">${escapeHtml(pinError)}</div>` : ''}
      <button type="button" class="gpt-primary-btn" id="gpt-submit-pin">Create PIN</button>
      <button type="button" class="gpt-link-btn" data-state-target="enter-pin">I already have a PIN</button>
      <div class="gpt-small-note">Welcome, ${escapeHtml(guest.name)}</div>
    </section>
  `;
}

function renderPinEnter(guest: GuestProfile): string {
  return `
    <section class="gpt-card gpt-auth-card">
      <div class="gpt-auth-icon">${iconLock()}</div>
      <h1>Enter your PIN</h1>
      <p>Return to Mitchell Space as ${escapeHtml(guest.name)}.</p>
      <label for="gpt-pin">PIN</label>
      <input id="gpt-pin" type="password" inputmode="numeric" autocomplete="current-password" maxlength="6" value="${escapeAttr(pinValue)}">
      ${pinError ? `<div class="gpt-error">${escapeHtml(pinError)}</div>` : ''}
      <button type="button" class="gpt-primary-btn" id="gpt-submit-pin">Continue</button>
      <button type="button" class="gpt-link-btn" id="gpt-forgot-pin">Forgot PIN?</button>
    </section>
  `;
}

function renderRequestAccess(guest: GuestProfile): string {
  return `
    <section class="gpt-card gpt-auth-card">
      <div class="gpt-auth-icon gpt-auth-icon-warn">${iconShieldLock()}</div>
      <h1>Access is currently disabled</h1>
      <p>Your access to Mitchell Space has been disabled by the administrator.</p>
      <div class="gpt-info-box">Mitchell can re-enable access or keep it disabled after reviewing your request.</div>
      <label for="gpt-request-message">Message to Mitchell</label>
      <textarea id="gpt-request-message" rows="5" placeholder="Message to Mitchell">${escapeHtml(requestMessage)}</textarea>
      ${requestSubmitted ? '<div class="gpt-success">Access request sent. Mitchell will review your request.</div>' : ''}
      <button type="button" class="gpt-primary-btn" id="gpt-request-access">Request access</button>
      <div class="gpt-small-note">Guest: ${escapeHtml(guest.name)}</div>
    </section>
  `;
}

function renderGuestHome(guest: GuestProfile): string {
  return `
    <section class="gpt-home">
      <header class="gpt-home-header">
        <div class="gpt-home-lock">${iconShieldLock()}</div>
        <div>
          <h1>Mitchell Space</h1>
          <p>Welcome, ${escapeHtml(guest.name)}</p>
        </div>
      </header>
      <div class="gpt-home-actions">
        <button type="button" class="gpt-home-action" data-state-target="text-channel">
          ${iconMessage()}
          <span>Text</span>
          <small>${escapeHtml(guest.textChannelName)}</small>
        </button>
        <button type="button" class="gpt-home-action" data-state-target="call-channel">
          ${iconPhone()}
          <span>Call</span>
          <small>${escapeHtml(guest.callChannelName)}</small>
        </button>
      </div>
      <footer class="gpt-home-footer">Private guest access for Mitchell Space. Security notices are shown before planned backend activation.</footer>
    </section>
  `;
}

function renderTextChannel(guest: GuestProfile): string {
  const replyMessage = replyToId ? messages.find((message) => message.id === replyToId) ?? null : null;
  return `
    <section class="gpt-chat">
      <header class="gpt-channel-header">
        <button type="button" class="gpt-icon-btn" data-state-target="guest-home" aria-label="Back to guest home">${iconChevronLeft()}</button>
        <div>
          <h1>${escapeHtml(guest.textChannelName)}</h1>
          <p>${escapeHtml(guest.categoryName)} guest text channel</p>
        </div>
      </header>
      <div class="gpt-chat-actions">
        <button type="button" class="gpt-secondary-btn" id="gpt-simulate-read">${iconDoubleCheck()}<span>Simulate read receipt</span></button>
      </div>
      <div class="gpt-message-area">
        ${renderPinnedGuestSystemNotices()}
        <div class="gpt-message-list" id="gpt-message-list">
          ${messages.map((message) => renderMessage(message, guest)).join('')}
        </div>
      </div>
      <form class="gpt-composer" id="gpt-composer">
        ${replyMessage ? `
          <div class="gpt-reply-preview">
            <div><strong>Replying to ${escapeHtml(replyMessage.sender)}</strong><span>${escapeHtml(replyMessage.content)}</span></div>
            <button type="button" class="gpt-icon-btn" id="gpt-clear-reply" aria-label="Clear reply">${iconX()}</button>
          </div>
        ` : ''}
        <div class="gpt-compose-row">
          <input id="gpt-message-input" type="text" autocomplete="off" maxlength="4000" placeholder="Message ${escapeAttr(guest.name)} channel" value="${escapeAttr(composerValue)}">
          <button type="submit" class="gpt-send-btn" aria-label="Send message">${iconSend()}</button>
        </div>
      </form>
    </section>
  `;
}

function renderPinnedGuestSystemNotices(): string {
  return `
    <div class="gpt-system-notices">
      <div class="gpt-system-notice gpt-system-notice-info">
        ${iconDoubleCheck()}
        <span>Messages will be erased 1 hour after read. This feature will activate once the backend retention worker is deployed.</span>
      </div>
      <div class="gpt-system-notice gpt-system-notice-warn">
        ${iconShieldLock()}
        <span>Screenshots are not permitted in this guest channel. Browser-level screenshot prevention is limited, and app-level capture notices will be shown where detectable.</span>
      </div>
      <div class="gpt-system-notice gpt-system-notice-info">
        ${iconBell()}
        <span>Browser notifications will be enabled for new messages in the final version.</span>
      </div>
    </div>
  `;
}

function renderMessage(message: GuestMessage, guest: GuestProfile): string {
  if (message.kind === 'system') {
    return `
      <article class="gpt-system-message" data-message-id="${escapeAttr(message.id)}">
        ${iconInfo()}
        <span>${escapeHtml(message.content)}</span>
      </article>
    `;
  }
  const mine = message.sender === guest.name;
  const reply = message.replyToId ? messages.find((candidate) => candidate.id === message.replyToId) ?? null : null;
  return `
    <article class="gpt-message ${mine ? 'gpt-message-guest' : 'gpt-message-mitchell'}" data-message-id="${escapeAttr(message.id)}">
      <button type="button" class="gpt-bubble" data-reply-id="${escapeAttr(message.id)}">
        <span class="gpt-sender">${escapeHtml(message.sender)}</span>
        ${reply ? `<span class="gpt-inline-reply">${escapeHtml(reply.sender)}: ${escapeHtml(reply.content)}</span>` : ''}
        <span class="gpt-message-text">${escapeHtml(message.content)}</span>
        <span class="gpt-message-meta"><time>${escapeHtml(formatTime(message.createdAt))}</time><span>${message.read ? `${iconDoubleCheck()} Read` : `${iconCheck()} Sent`}</span></span>
      </button>
    </article>
  `;
}

function renderCallChannel(guest: GuestProfile): string {
  const duration = callState === 'connected' || callState === 'reconnecting' ? currentCallDurationSeconds() : endedDurationSeconds;
  return `
    <section class="gpt-call">
      <header class="gpt-call-header">
        <button type="button" class="gpt-icon-btn" data-state-target="guest-home" aria-label="Back to guest home">${iconChevronLeft()}</button>
        <div>
          <h1>${escapeHtml(guest.callChannelName)}</h1>
          <p>Mitchell Space</p>
        </div>
      </header>
      <div class="gpt-call-stage gpt-call-state-${escapeAttr(callState)}">
        ${renderCallStage(duration)}
      </div>
      <div class="gpt-call-security">
        <span>${iconLock()} WebRTC encrypted media</span>
        <span>${iconShieldLock()} Encrypted in transit</span>
      </div>
    </section>
  `;
}

function renderCallStage(duration: number): string {
  if (callState === 'ready') {
    return `
      <button type="button" class="gpt-call-main-btn" id="gpt-start-call">${iconPhone()}<span>Call Mitchell</span></button>
    `;
  }
  if (callState === 'ringing') {
    return `
      <div class="gpt-pulse">${iconPhone()}</div>
      <h2>Calling Mitchell...</h2>
      <button type="button" class="gpt-secondary-btn" id="gpt-cancel-call">Cancel</button>
    `;
  }
  if (callState === 'connected') {
    return `
      <h2>Connected</h2>
      <div class="gpt-call-timer">${escapeHtml(formatDuration(duration))}</div>
      ${renderConnectedCallButtons()}
    `;
  }
  if (callState === 'reconnecting') {
    return `
      <div class="gpt-spinner" aria-hidden="true"></div>
      <h2>Reconnecting...</h2>
      <div class="gpt-call-timer">${escapeHtml(formatDuration(duration))}</div>
      ${renderConnectedCallButtons()}
    `;
  }
  return `
    <h2>Call ended</h2>
    <div class="gpt-call-timer">${escapeHtml(formatDuration(duration))}</div>
    <button type="button" class="gpt-call-main-btn" id="gpt-start-call">${iconPhone()}<span>Call again</span></button>
  `;
}

function renderConnectedCallButtons(): string {
  return `
    <div class="gpt-call-controls">
      <button type="button" class="gpt-round-btn ${callMuted ? 'gpt-round-active' : ''}" id="gpt-toggle-mute" aria-label="Mute">${callMuted ? iconMicOff() : iconMic()}<span>Mute</span></button>
      <button type="button" class="gpt-round-btn ${speakerEnabled ? 'gpt-round-active' : ''}" id="gpt-toggle-speaker" aria-label="Speaker">${iconSpeaker()}<span>Speaker</span></button>
      <button type="button" class="gpt-round-btn gpt-end-btn" id="gpt-end-call" aria-label="End call">${iconPhoneDown()}<span>End</span></button>
    </div>
  `;
}

function renderNoticePage(_guest: GuestProfile, title: string, body: string, icon: string): string {
  return `
    <section class="gpt-card gpt-notice-page">
      <div class="gpt-auth-icon">${icon}</div>
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(body)}</p>
      <button type="button" class="gpt-primary-btn" data-state-target="guest-home">Back to guest home</button>
    </section>
  `;
}

function wirePage(): void {
  document.getElementById('gpt-state-select')?.addEventListener('change', (event) => {
    const value = (event.target as HTMLSelectElement).value;
    if (isGuestPreviewState(value)) setPreviewState(value);
  });
  document.getElementById('gpt-prev-state')?.addEventListener('click', () => moveState(-1));
  document.getElementById('gpt-next-state')?.addEventListener('click', () => moveState(1));
  document.querySelectorAll<HTMLButtonElement>('[data-state-target]').forEach((button) => {
    button.addEventListener('click', () => {
      const target = button.dataset['stateTarget'];
      if (isGuestPreviewState(target)) setPreviewState(target);
    });
  });
  wirePinInputs();
  wireRequestAccess();
  wireChat();
  wireCall();
}

function wirePinInputs(): void {
  document.getElementById('gpt-pin')?.addEventListener('input', (event) => {
    pinValue = digitsOnly((event.target as HTMLInputElement).value);
    (event.target as HTMLInputElement).value = pinValue;
  });
  document.getElementById('gpt-pin-confirm')?.addEventListener('input', (event) => {
    pinConfirmValue = digitsOnly((event.target as HTMLInputElement).value);
    (event.target as HTMLInputElement).value = pinConfirmValue;
  });
  document.getElementById('gpt-submit-pin')?.addEventListener('click', submitPin);
  document.getElementById('gpt-forgot-pin')?.addEventListener('click', () => setPreviewState('request-access'));
}

function wireRequestAccess(): void {
  document.getElementById('gpt-request-message')?.addEventListener('input', (event) => {
    requestMessage = (event.target as HTMLTextAreaElement).value;
  });
  document.getElementById('gpt-request-access')?.addEventListener('click', () => {
    requestSubmitted = true;
    paint();
  });
}

function wireChat(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-reply-id]').forEach((button) => {
    button.addEventListener('click', () => {
      replyToId = button.dataset['replyId'] ?? null;
      paint();
    });
  });
  document.getElementById('gpt-clear-reply')?.addEventListener('click', () => {
    replyToId = null;
    paint();
  });
  document.getElementById('gpt-message-input')?.addEventListener('input', (event) => {
    composerValue = (event.target as HTMLInputElement).value;
  });
  document.getElementById('gpt-composer')?.addEventListener('submit', (event) => {
    event.preventDefault();
    sendMockMessage(guestForRoute());
  });
  document.getElementById('gpt-simulate-read')?.addEventListener('click', () => {
    messages = messages.map((message) => message.kind === 'user' ? { ...message, read: true } : message);
    paint();
  });
}

function wireCall(): void {
  document.getElementById('gpt-start-call')?.addEventListener('click', startCall);
  document.getElementById('gpt-cancel-call')?.addEventListener('click', () => {
    callState = 'ended';
    endedDurationSeconds = 0;
    clearCallTimers();
    paint();
  });
  document.getElementById('gpt-end-call')?.addEventListener('click', endCall);
  document.getElementById('gpt-toggle-mute')?.addEventListener('click', () => {
    callMuted = !callMuted;
    paint();
  });
  document.getElementById('gpt-toggle-speaker')?.addEventListener('click', () => {
    speakerEnabled = !speakerEnabled;
    paint();
  });
}

function setPreviewState(next: GuestPreviewState): void {
  if (currentState === 'call-channel' && next !== 'call-channel') clearCallTimers();
  currentState = next;
  pinError = '';
  if (next === 'call-channel' && callState === 'connected') ensureCallTicking();
  paint();
}

function moveState(delta: number): void {
  const index = TEST_STATES.findIndex((state) => state.value === currentState);
  const nextIndex = (index + delta + TEST_STATES.length) % TEST_STATES.length;
  setPreviewState(TEST_STATES[nextIndex]?.value ?? 'create-pin');
}

function submitPin(): void {
  if (!/^\d{4,6}$/.test(pinValue)) {
    pinError = 'Enter a 4-6 digit PIN.';
    paint();
    return;
  }
  if (currentState === 'create-pin' && pinValue !== pinConfirmValue) {
    pinError = 'PIN entries do not match.';
    paint();
    return;
  }
  pinValue = '';
  pinConfirmValue = '';
  pinError = '';
  setPreviewState('guest-home');
}

function sendMockMessage(guest: GuestProfile): void {
  const content = composerValue.trim();
  if (!content) return;
  messages = [
    ...messages,
    {
      id: `mock-${Date.now()}-${messages.length}`,
      kind: 'user',
      sender: guest.name,
      content,
      createdAt: new Date().toISOString(),
      read: false,
      replyToId,
    },
  ];
  composerValue = '';
  replyToId = null;
  paint();
}

function startCall(): void {
  clearCallTimers();
  callState = 'ringing';
  connectedStartedAt = 0;
  elapsedBeforeCurrentConnection = 0;
  endedDurationSeconds = 0;
  paint();
  ringingTimerId = window.setTimeout(() => {
    callState = 'connected';
    connectedStartedAt = Date.now();
    elapsedBeforeCurrentConnection = 0;
    ensureCallTicking();
    paint();
  }, 2000);
}

function endCall(): void {
  endedDurationSeconds = currentCallDurationSeconds();
  callState = 'ended';
  clearCallTimers();
  paint();
}

function ensureCallTicking(): void {
  if (callTimerId !== null) return;
  callTimerId = window.setInterval(() => {
    renderTick += 1;
    if (renderTick > 1000000) renderTick = 0;
    if (currentState === 'call-channel' && (callState === 'connected' || callState === 'reconnecting')) paint();
  }, 1000);
  reconnectTimerId = window.setTimeout(() => {
    if (callState !== 'connected') return;
    elapsedBeforeCurrentConnection = currentCallDurationSeconds();
    connectedStartedAt = Date.now();
    callState = 'reconnecting';
    paint();
    window.setTimeout(() => {
      if (callState !== 'reconnecting') return;
      callState = 'connected';
      connectedStartedAt = Date.now();
      paint();
    }, 1800);
  }, 9000);
}

function currentCallDurationSeconds(): number {
  if (connectedStartedAt === 0) return elapsedBeforeCurrentConnection;
  return elapsedBeforeCurrentConnection + Math.max(0, Math.floor((Date.now() - connectedStartedAt) / 1000));
}

function clearCallTimers(): void {
  if (callTimerId !== null) window.clearInterval(callTimerId);
  if (ringingTimerId !== null) window.clearTimeout(ringingTimerId);
  if (reconnectTimerId !== null) window.clearTimeout(reconnectTimerId);
  callTimerId = null;
  ringingTimerId = null;
  reconnectTimerId = null;
}

function mockMessagesForGuest(guest: GuestProfile): GuestMessage[] {
  const now = Date.now();
  return [
    { id: 'm1', kind: 'user', sender: 'Mitchell', content: 'Hey, welcome to your space. Feel free to message anytime.', createdAt: new Date(now - 1000 * 60 * 12).toISOString(), read: true, replyToId: null },
    { id: 'm2', kind: 'user', sender: guest.name, content: 'Thanks Mitchell, this is great.', createdAt: new Date(now - 1000 * 60 * 10).toISOString(), read: true, replyToId: null },
    { id: 'm3', kind: 'user', sender: 'Mitchell', content: 'Let me know if you need anything.', createdAt: new Date(now - 1000 * 60 * 7).toISOString(), read: false, replyToId: null },
  ];
}

function isGuestPreviewState(value: string | undefined): value is GuestPreviewState {
  return TEST_STATES.some((state) => state.value === value);
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
}

function escapeHtml(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function svg(body: string): string {
  return `<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
}

function iconMessage(): string { return svg('<path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>'); }
function iconPhone(): string { return svg('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.64a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.44-1.19a2 2 0 0 1 2.11-.45c.84.29 1.73.5 2.64.62A2 2 0 0 1 22 16.92z"/>'); }
function iconPhoneDown(): string { return svg('<path d="M10.68 13.31a16 16 0 0 0 2.64 0l1.25 2.58a2 2 0 0 0 2.27 1.06l3.16-.79a2 2 0 0 0 1.5-1.94v-2.44a2 2 0 0 0-1.12-1.79A19 19 0 0 0 12 8a19 19 0 0 0-8.38 1.99A2 2 0 0 0 2.5 11.78v2.44a2 2 0 0 0 1.5 1.94l3.16.79a2 2 0 0 0 2.27-1.06l1.25-2.58z"/>'); }
function iconLock(): string { return svg('<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>'); }
function iconShieldLock(): string { return svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><rect x="9" y="11" width="6" height="5" rx="1"/><path d="M10 11V9a2 2 0 0 1 4 0v2"/>'); }
function iconChevronLeft(): string { return svg('<path d="M15 18l-6-6 6-6"/>'); }
function iconChevronRight(): string { return svg('<path d="M9 18l6-6-6-6"/>'); }
function iconSend(): string { return svg('<path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>'); }
function iconCheck(): string { return svg('<path d="M20 6L9 17l-5-5"/>'); }
function iconDoubleCheck(): string { return svg('<path d="M20 6L9 17l-5-5"/><path d="M16 6l-7 7-2-2"/>'); }
function iconInfo(): string { return svg('<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'); }
function iconBell(): string { return svg('<path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>'); }
function iconX(): string { return svg('<path d="M18 6L6 18"/><path d="M6 6l12 12"/>'); }
function iconMic(): string { return svg('<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><path d="M12 19v3"/>'); }
function iconMicOff(): string { return svg('<path d="M2 2l20 20"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><path d="M15 9.34V5a3 3 0 0 0-5.94-.6"/><path d="M19 10v2a7 7 0 0 1-.89 3.41"/><path d="M5 10v2a7 7 0 0 0 11.95 4.95"/><path d="M12 19v3"/>'); }
function iconSpeaker(): string { return svg('<path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.08"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>'); }

function mountGuestPreviewStyles(): void {
  const styleId = 'guest-preview-test-v2-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
.guest-preview-test-body #app { min-height: 100dvh; background: var(--surface-2); color: var(--text-primary); }
.gpt-shell { min-height: 100dvh; width: min(100%, 760px); margin: 0 auto; padding: 12px; box-sizing: border-box; display: grid; align-content: start; gap: 10px; }
.gpt-test-banner, .gpt-state-panel, .gpt-card, .gpt-home, .gpt-chat, .gpt-call { border: 1px solid var(--border-subtle); background: var(--surface-1); border-radius: var(--radius-md); }
.gpt-test-banner { display: grid; gap: 4px; padding: 10px 12px; font-size: 13px; color: var(--text-secondary); }
.gpt-test-kicker { color: var(--warning); font-weight: 800; text-transform: uppercase; }
.gpt-test-copy { color: var(--text-primary); }
.gpt-final-url { display: grid; gap: 3px; color: var(--text-muted); }
.gpt-final-url code { color: var(--link); overflow-wrap: anywhere; }
.gpt-state-panel { display: grid; gap: 8px; padding: 10px; }
.gpt-state-panel label, .gpt-card label { color: var(--text-secondary); font-size: 13px; font-weight: 700; }
.gpt-state-row { display: grid; grid-template-columns: 1fr 42px 42px; gap: 8px; }
.gpt-state-row select, .gpt-card input, .gpt-card textarea, .gpt-compose-row input { min-width: 0; width: 100%; box-sizing: border-box; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-2); color: var(--text-primary); padding: 12px; font: inherit; letter-spacing: 0; }
.gpt-card { padding: 18px; display: grid; gap: 12px; }
.gpt-auth-card, .gpt-notice-page { min-height: 420px; align-content: center; }
.gpt-card h1, .gpt-home h1, .gpt-channel-header h1, .gpt-call-header h1, .gpt-call-stage h2 { margin: 0; color: var(--text-primary); letter-spacing: 0; }
.gpt-card p, .gpt-home p, .gpt-channel-header p, .gpt-call-header p { margin: 4px 0 0; color: var(--text-secondary); line-height: 1.45; }
.gpt-auth-icon, .gpt-home-lock { width: 52px; height: 52px; display: grid; place-items: center; border: 1px solid var(--border-subtle); border-radius: var(--radius-md); color: var(--accent); background: var(--accent-soft); }
.gpt-auth-icon-warn { color: var(--warning); background: var(--surface-2); }
.gpt-primary-btn, .gpt-secondary-btn, .gpt-link-btn, .gpt-home-action, .gpt-icon-btn, .gpt-send-btn, .gpt-bubble, .gpt-call-main-btn, .gpt-round-btn { border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); color: var(--text-primary); background: var(--surface-2); cursor: pointer; font: inherit; letter-spacing: 0; }
.gpt-primary-btn { min-height: 46px; background: var(--accent); color: var(--fg-on-accent); font-weight: 800; }
.gpt-primary-btn:hover { background: var(--accent-hover); }
.gpt-secondary-btn { min-height: 42px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 8px 10px; }
.gpt-secondary-btn:hover, .gpt-icon-btn:hover, .gpt-home-action:hover, .gpt-bubble:hover { background: var(--surface-2-hover); }
.gpt-link-btn { border-color: transparent; color: var(--link); background: transparent; min-height: 38px; justify-self: start; }
.gpt-icon-btn { width: 42px; height: 42px; display: grid; place-items: center; padding: 0; }
.gpt-error { color: var(--danger); background: var(--danger-soft); border: 1px solid var(--danger); border-radius: var(--radius-sm); padding: 10px; }
.gpt-success, .gpt-info-box { color: var(--text-primary); background: var(--info-soft); border: 1px solid var(--info); border-radius: var(--radius-sm); padding: 10px; line-height: 1.45; }
.gpt-small-note { color: var(--text-muted); font-size: 13px; }
.gpt-home { min-height: 520px; padding: 18px; display: grid; grid-template-rows: auto 1fr auto; gap: 18px; }
.gpt-home-header { display: flex; align-items: center; gap: 12px; }
.gpt-home-actions { display: grid; gap: 12px; align-content: center; }
.gpt-home-action { min-height: 132px; display: grid; align-content: center; justify-items: start; gap: 8px; padding: 18px; text-align: left; background: var(--surface-2); }
.gpt-home-action svg { color: var(--accent); width: 30px; height: 30px; }
.gpt-home-action span { font-size: 22px; font-weight: 800; }
.gpt-home-action small, .gpt-home-footer { color: var(--text-muted); line-height: 1.45; }
.gpt-chat { min-height: calc(100dvh - 134px); display: grid; grid-template-rows: auto auto 1fr auto; overflow: hidden; }
.gpt-channel-header, .gpt-call-header { display: flex; gap: 10px; align-items: center; padding: 12px; border-bottom: 1px solid var(--border-subtle); }
.gpt-chat-actions { padding: 10px 12px 0; }
.gpt-message-area { min-height: 0; overflow-y: auto; display: grid; grid-template-rows: auto 1fr; }
.gpt-system-notices { position: sticky; top: 0; z-index: 2; display: grid; gap: 8px; padding: 10px 12px 8px; background: var(--surface-1); border-bottom: 1px solid var(--border-subtle); }
.gpt-system-notice { display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); line-height: 1.45; font-size: 13px; color: var(--text-secondary); }
.gpt-system-notice svg { width: 16px; height: 16px; flex: 0 0 auto; margin-top: 1px; }
.gpt-system-notice-info { background: var(--info-soft); }
.gpt-system-notice-info svg { color: var(--info); }
.gpt-system-notice-warn { background: var(--warning-soft); }
.gpt-system-notice-warn svg { color: var(--warning); }
.gpt-message-list { min-height: 0; display: flex; flex-direction: column; gap: 10px; padding: 12px; }
.gpt-message { display: flex; }
.gpt-message-mitchell { justify-content: flex-end; }
.gpt-message-guest { justify-content: flex-start; }
.gpt-bubble { max-width: min(82%, 430px); display: grid; gap: 5px; padding: 10px 12px; text-align: left; line-height: 1.35; overflow-wrap: anywhere; }
.gpt-message-mitchell .gpt-bubble { background: var(--accent-soft); border-color: var(--accent); }
.gpt-message-guest .gpt-bubble { background: var(--surface-2); }
.gpt-sender { color: var(--text-secondary); font-size: 12px; font-weight: 800; }
.gpt-inline-reply { border-left: 2px solid var(--border-strong); padding-left: 8px; color: var(--text-muted); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gpt-message-text { color: var(--text-primary); }
.gpt-message-meta { display: inline-flex; align-items: center; gap: 8px; color: var(--text-muted); font-size: 12px; }
.gpt-message-meta span { display: inline-flex; align-items: center; gap: 3px; }
.gpt-message-meta svg { width: 14px; height: 14px; color: var(--accent); }
.gpt-system-message { align-self: center; max-width: 92%; display: inline-flex; align-items: center; gap: 8px; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-3); color: var(--text-secondary); padding: 8px 10px; font-size: 13px; line-height: 1.35; }
.gpt-system-message svg { color: var(--info); flex: 0 0 auto; }
.gpt-composer { border-top: 1px solid var(--border-subtle); padding: 10px 12px 12px; display: grid; gap: 8px; background: var(--surface-1); }
.gpt-reply-preview { display: grid; grid-template-columns: 1fr 42px; gap: 8px; align-items: center; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); background: var(--surface-2); padding: 8px; }
.gpt-reply-preview div { min-width: 0; display: grid; gap: 2px; }
.gpt-reply-preview strong { color: var(--text-secondary); font-size: 12px; }
.gpt-reply-preview span { color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gpt-compose-row { display: grid; grid-template-columns: 1fr 48px; gap: 8px; }
.gpt-send-btn { display: grid; place-items: center; background: var(--accent); color: var(--fg-on-accent); }
.gpt-call { min-height: calc(100dvh - 134px); display: grid; grid-template-rows: auto 1fr auto; overflow: hidden; background: var(--surface-0); }
.gpt-call-stage { display: grid; place-items: center; align-content: center; gap: 18px; padding: 24px; text-align: center; }
.gpt-call-main-btn { width: min(100%, 280px); min-height: 74px; display: inline-flex; align-items: center; justify-content: center; gap: 12px; background: var(--accent); color: var(--fg-on-accent); font-weight: 900; font-size: 20px; border-radius: var(--radius-md); }
.gpt-call-timer { color: var(--text-primary); font-size: 42px; font-weight: 900; font-variant-numeric: tabular-nums; }
.gpt-call-controls { display: flex; justify-content: center; flex-wrap: wrap; gap: 14px; }
.gpt-round-btn { width: 78px; height: 78px; border-radius: var(--radius-md); display: grid; place-items: center; align-content: center; gap: 4px; color: var(--text-secondary); }
.gpt-round-btn span { font-size: 12px; }
.gpt-round-active { color: var(--accent); border-color: var(--accent); background: var(--accent-soft); }
.gpt-end-btn { color: var(--fg-on-accent); border-color: var(--danger); background: var(--danger); }
.gpt-call-security { display: grid; gap: 6px; justify-items: center; padding: 12px; color: var(--text-muted); font-size: 13px; border-top: 1px solid var(--border-subtle); }
.gpt-call-security span { display: inline-flex; align-items: center; gap: 6px; }
.gpt-call-security svg { width: 16px; height: 16px; color: var(--accent); }
.gpt-pulse { width: 92px; height: 92px; display: grid; place-items: center; border-radius: var(--radius-lg); background: var(--accent); color: var(--fg-on-accent); animation: gpt-pulse 1.2s ease-in-out infinite; }
.gpt-spinner { width: 58px; height: 58px; border-radius: var(--radius-lg); border: 4px solid var(--border-subtle); border-top-color: var(--accent); animation: gpt-spin 0.9s linear infinite; }
@keyframes gpt-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.78; } }
@keyframes gpt-spin { to { transform: rotate(360deg); } }
@media (min-width: 680px) {
  .gpt-shell { padding: 18px; }
  .gpt-home-actions { grid-template-columns: 1fr 1fr; }
  .gpt-test-banner { grid-template-columns: auto 1fr; align-items: center; }
  .gpt-final-url { grid-column: 1 / -1; }
}
`;
  document.head.appendChild(style);
}
